import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getSuperAdminStudent, SAStudentProfile } from '../../services/api';
import SuperAdminLayout from './SuperAdminLayout';

const PILL: Record<string, string> = { got: 'good', almost: 'warn', needs: 'crit', none: 'neutral' };
const LABEL: Record<string, string> = { got: 'Got it', almost: 'Almost', needs: 'Needs practice', none: 'No data yet' };
const CAT: Record<string, string> = { bright: 'Bright', hardworking: 'Hardworking', disengaged: 'Disengaged', unknown: 'Not yet categorised' };
const STATE_PILL: Record<string, string> = { solid: 'good', partly_solid: 'warn', forgotten: 'crit', never_learned: 'crit', unknown: 'neutral' };
const STATE_LABEL: Record<string, string> = { solid: 'Got it', partly_solid: 'Almost', forgotten: 'Forgotten', never_learned: 'Needs practice', unknown: '—' };

/** Task 147 — one student's understanding picture (artifact styling). */
const SuperAdminStudent: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [d, setD] = useState<SAStudentProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('superAdminToken');
    if (!token) {
      navigate('/super-admin/login', { replace: true });
      return;
    }
    if (!id) return;
    getSuperAdminStudent(token, Number(id))
      .then(setD)
      .catch((e) => {
        const msg = e instanceof Error ? e.message : 'Failed to load.';
        setError(msg);
        if (/token|unauth/i.test(msg)) {
          localStorage.removeItem('superAdminToken');
          navigate('/super-admin/login', { replace: true });
        }
      })
      .finally(() => setLoading(false));
  }, [navigate, id]);

  const clsName = (g: number, s?: string | null) => `Grade ${g}${s ? `-${s}` : ''}`;

  return (
    <SuperAdminLayout>
      <div className="content">
        <div className="back" onClick={() => navigate(-1)}>‹ Back</div>
        {loading ? (
          <p className="hint">Loading…</p>
        ) : error || !d ? (
          <p className="hint" style={{ color: 'var(--crit)' }}>{error || 'Not found.'}</p>
        ) : (
          <>
            <div className="card">
              <div className="prof-head">
                <span className="avatar">{d.name.slice(0, 2).toUpperCase()}</span>
                <div className="meta">
                  <h3>{d.name} <span className={`pill ${PILL[d.bucket] || 'neutral'}`}>{LABEL[d.bucket] || d.bucket}</span></h3>
                  <p>{clsName(d.grade, d.section)} · {d.school}{d.roll != null ? ` · roll ${d.roll}` : ''}</p>
                </div>
              </div>
            </div>

            <div className="tilerow mt14">
              <div className="tile"><div className="lab">Category</div><div className="val" style={{ fontSize: '15px' }}>{CAT[d.category] || d.category}</div></div>
              <div className="tile"><div className="lab">Curiosity</div><div className="val">{d.curiosity ?? '—'}</div></div>
              <div className="tile"><div className="lab">Quiz avg</div><div className="val">{d.quiz != null ? `${d.quiz}%` : '—'}</div></div>
              <div className="tile"><div className="lab">Topics seen</div><div className="val">{d.topics.length}</div></div>
            </div>

            <div className="card mt14">
              <h3>Understanding by topic <span className="ai">AI-assessed</span></h3>
              <p className="sub">From their coach conversations — struggling topics first.</p>
              {d.topics.length === 0 ? (
                <p className="hint">No AI signal yet — this student hasn’t chatted enough for an assessment.</p>
              ) : (
                <div className="ivl">
                  {d.topics.map((t, i) => (
                    <div key={i} className="iv" style={{ cursor: 'default' }}>
                      <span className="what"><b style={{ fontWeight: 600 }}>{t.topic}</b>{t.subject ? <small>{t.subject}</small> : null}</span>
                      <span className={`pill ${STATE_PILL[t.state] || 'neutral'}`}>{STATE_LABEL[t.state] || t.state}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminStudent;
